import React from 'react';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '@maxtropy/components';
import electricityIcon from '../../assets/medium-electricity.png';
import naturalGasIcon from '../../assets/medium-natural-gas.png';
import compressedAirIcon from '../../assets/medium-compressed-air.png';
import carbonDioxideIcon from '../../assets/medium-carbon-dioxide.png';
import oxygenIcon from '../../assets/medium-oxygen.png';
import argonIcon from '../../assets/medium-argon.png';
import type { AnalysisViewMode, PeriodMode } from '../../types';
import styles from './index.module.scss';

export const MEDIUMS = [
  { value: 0, label: '电力', icon: electricityIcon },
  { value: 1, label: '天然气', icon: naturalGasIcon },
  { value: 2, label: '压缩空气', icon: compressedAirIcon },
  { value: 3, label: '二氧化碳', icon: carbonDioxideIcon },
  { value: 4, label: '氧气', icon: oxygenIcon },
  { value: 5, label: '氩气', icon: argonIcon },
] as const;

const PERIODS: { value: PeriodMode; label: string }[] = [
  { value: 'year', label: '年度' },
  { value: 'month', label: '月度' },
  { value: 'day', label: '今日' },
];

interface MediumToolbarProps {
  viewMode: AnalysisViewMode;
  mediumType: number;
  periodMode: PeriodMode;
  date: Dayjs;
  counts: Map<number, number>;
  onViewModeChange: (value: AnalysisViewMode) => void;
  onMediumChange: (value: number) => void;
  onPeriodModeChange: (value: PeriodMode) => void;
  onDateChange: (value: Dayjs) => void;
}

const MediumToolbar: React.FC<MediumToolbarProps> = ({
  viewMode,
  mediumType,
  periodMode,
  date,
  counts,
  onViewModeChange,
  onMediumChange,
  onPeriodModeChange,
  onDateChange,
}) => {
  const picker = periodMode === 'year' ? 'year' : periodMode === 'month' ? 'month' : 'date';
  const cutoff =
    periodMode === 'day' ? dayjs().format('YYYY-MM-DD HH:mm') : date.endOf(periodMode).format('YYYY-MM-DD HH:mm');

  return (
    <div className={styles.toolbar}>
      <div className={styles.dimensionSwitch}>
        <div
          className={classNames(styles.dimensionItem, viewMode === 'medium' && styles.dimensionActive)}
          onClick={() => onViewModeChange('medium')}
        >
          按介质
        </div>
        <div
          className={classNames(styles.dimensionItem, viewMode === 'area' && styles.dimensionActive)}
          onClick={() => onViewModeChange('area')}
        >
          按区域
        </div>
      </div>
      {viewMode === 'medium' ? (
        <div className={styles.mediums}>
          {MEDIUMS.map(item => (
            <div
              key={item.value}
              className={classNames(styles.mediumItem, mediumType === item.value && styles.mediumActive)}
              onClick={() => onMediumChange(item.value)}
            >
              <img src={item.icon} alt="" />
              <span className={styles.mediumName}>{item.label}</span>
              <span className={styles.mediumCount}>
                <span className={styles.mediumCountValue}>{counts.get(item.value) ?? '--'}</span>
                <span className={styles.mediumCountUnit}> 个统计层级</span>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.mediumPlaceholder} />
      )}
      <div className={styles.periodControl}>
        <div className={styles.periods}>
          {PERIODS.map(item => (
            <div
              key={item.value}
              className={classNames(styles.periodItem, periodMode === item.value && styles.active)}
              onClick={() => onPeriodModeChange(item.value)}
            >
              {item.label}
            </div>
          ))}
        </div>
        <DatePicker
          value={periodMode === 'day' ? dayjs() : date}
          picker={picker}
          style={{ width: 140 }}
          allowClear={false}
          disabled={periodMode === 'day'}
          onChange={value => value && onDateChange(value)}
        />
        <span className={styles.cutoff}>数据截至：</span>
        <span className={styles.cutdate}>{cutoff}</span>
      </div>
    </div>
  );
};

export default MediumToolbar;
